import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MediaLike } from "../types/media";
import { mediaService } from "../services/mediaService";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import Session from "supertokens-auth-react/recipe/session";

export const mediaLikeKeys = {
  all: ['mediaLikes'] as const,
  lists: () => [...mediaLikeKeys.all, 'list'] as const,
  list: (ownerId: string, albumId: string, mediaId: string) => [...mediaLikeKeys.lists(), ownerId, albumId, mediaId] as const,
};

interface UseMediaLikesReturn {
  likes: MediaLike[];
  likesCount: number;
  currentUserLike: MediaLike | null;
  isLikedByCurrentUser: boolean;
  isLoading: boolean;
  isToggling: boolean;
  error: string | null;
  toggleLike: () => Promise<void>;
  refreshLikes: () => Promise<void>;
  clearError: () => void;
}

export const useMediaLikes = (
  ownerId: string | undefined,
  albumId: string | undefined,
  mediaId: string | undefined,
  currentUserId: string | undefined
): UseMediaLikesReturn => {
  const queryClient = useQueryClient();

  const {
    data: likes = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: mediaLikeKeys.list(ownerId!, albumId!, mediaId!),
    queryFn: async () => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("User not authenticated or missing parameters");
      }
      return mediaService.getMediaLikes(ownerId, albumId, mediaId);
    },
    enabled: !!ownerId && !!albumId && !!mediaId,
  });

  const currentUserLike = likes.find(like => like.userId === currentUserId) || null;
  const isLikedByCurrentUser = currentUserLike !== null;
  const likesCount = likes.length;

  const createLikeMutation = useMutation({
    mutationFn: async () => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("User not authenticated or missing parameters");
      }
      return mediaService.createMediaLike(ownerId, albumId, mediaId);
    },
    onSuccess: (newLike) => {
      queryClient.setQueryData<MediaLike[]>(mediaLikeKeys.list(ownerId!, albumId!, mediaId!), (old) => {
        return old ? [...old, newLike] : [newLike];
      });
    },
    onError: (error) => {
      console.error("Error creating media like:", handleApiError(error));
    },
  });

  const deleteLikeMutation = useMutation({
    mutationFn: async (likeId: string) => {
      const sessionExists =
        ownerId && albumId && mediaId ? await Session.doesSessionExist() : false;
      if (!ownerId || !albumId || !mediaId || !sessionExists) {
        throw new Error("User not authenticated or missing parameters");
      }
      return mediaService.deleteMediaLike(ownerId, albumId, mediaId, likeId);
    },
    onSuccess: (_, likeId) => {
      queryClient.setQueryData<MediaLike[]>(mediaLikeKeys.list(ownerId!, albumId!, mediaId!), (old) => {
        return old ? old.filter((like) => like.id !== likeId) : [];
      });
    },
    onError: (error) => {
      console.error("Error deleting media like:", handleApiError(error));
    },
  });

  const toggleLike = async () => {
    if (!ownerId || !albumId || !mediaId || !currentUserId) return;

    if (isLikedByCurrentUser && currentUserLike) {
      await deleteLikeMutation.mutateAsync(currentUserLike.id);
    } else {
      await createLikeMutation.mutateAsync();
    }
  };

  return {
    // Data
    likes,
    likesCount,
    currentUserLike,
    isLikedByCurrentUser,

    // Loading states
    isLoading,
    isToggling: createLikeMutation.isPending || deleteLikeMutation.isPending,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : null,

    // Actions
    toggleLike,
    refreshLikes: async () => { await refetch(); },
    clearError: () => {
      queryClient.resetQueries({ queryKey: mediaLikeKeys.list(ownerId!, albumId!, mediaId!) });
    },
  };
};